// @flow

import * as React from 'react';

import moment from 'moment';

import { selectTaxesDisplaySetting } from '@gandi/authentication';
import type { Currency } from '@gandi/billing';
import { useTranslator, type Locale } from '@gandi/translate';
import { Base, Button, Choice, TooltipContainer, Tooltip } from '@gandi/ui';

import { useAppSelector } from 'src/hooks/useAppSelector';

import type { Phase, TaxedPrice } from '../../types';

import BadgeList from './BadgeList';
import { getTranslation } from './translations';

export type Props = {
  checked: boolean,
  currency: Currency,
  locale: Locale,
  phase: Phase,
  price: ?TaxedPrice,
  recommended: boolean,
  tld: string,
  userTimeZone: string,
};

function PhaseChoice({
  checked,
  currency,
  locale,
  phase,
  price,
  recommended,
  tld,
  userTimeZone,
}: Props): React.MixedElement {
  const { __, formatDate, formatPrice } = useTranslator();
  const taxesIncluded = useAppSelector(selectTaxesDisplaySetting);
  const priceKey = taxesIncluded ? 'afterTaxes' : 'beforeTaxes';
  const now = new Date().getTime();

  const displayPrice = price?.[priceKey];
  const isClosed = phase.ends_at
    ? now > new Date(phase.ends_at).getTime()
    : false;
  const onGoing = now > new Date(phase.starts_at || '').getTime() && !isClosed;

  const getDateWithUTCOffset: () => string = () => {
    const dateFormat = 'lll UTC Z';

    const dateStart = phase.starts_at || '';
    const dateEnd = phase.ends_at;

    const start = new Date(dateStart).getTime();

    if (!!dateEnd && (isClosed || now > start)) {
      return formatDate(dateEnd, dateFormat, false);
    }

    return formatDate(dateStart, dateFormat, false);
  };

  const getDateLabel: () => string = () => {
    const dateStart = new Date(phase.starts_at || '');
    const dateEnd = phase.ends_at ? new Date(phase.ends_at) : undefined;

    /*
      `isClosed` is computed from `phase?.ends_at`
      If `phase.ends_at` exists then `isClosed` can be truthy, and `dateEnd` will be defined
    */
    if (isClosed && dateEnd) {
      return __('Closed since %(date)s', {
        date: formatDate(dateEnd, 'lll', false),
      });
    }

    const start = dateStart.getTime();
    const dateFormat = 'lll';

    if (!!dateEnd && start > now && dateStart) {
      const end = dateEnd.getTime();
      // Force moment to use the correct locale...
      moment.locale(locale);
      return __('Start %(date)s, for %(duration)s', {
        date: formatDate(dateStart, dateFormat, false),
        duration: moment(start).from(end, true),
      });
    }

    if (!!dateEnd && now > start) {
      return __('End %(date)s', {
        date: formatDate(dateEnd, dateFormat, false),
      });
    }

    return __('Start %(date)s', {
      date: formatDate(dateStart, dateFormat, false),
    });
  };

  const getButtonLabel: () => string = () => {
    const dateStart = new Date(phase.starts_at || '');
    const start = new Date(dateStart).getTime();

    if (start > now) {
      if (displayPrice) {
        return __('Pre register for %(price)s', {
          price: formatPrice(displayPrice, currency),
        });
      }

      return __('Pre register');
    }

    if (displayPrice) {
      return __('Buy for %(price)s', {
        price: formatPrice(displayPrice, currency),
      });
    }

    return __('Buy');
  };

  return (
    <>
      <Choice disabled={isClosed} value={phase.name}>
        <Base gap="xsmall" flex flexRow slot="label">
          {getTranslation(__, phase.name, tld)}

          <BadgeList
            isRecommended={recommended}
            isRestricted={phase.name === 'sunrise'}
            isClosed={isClosed}
            onGoing={onGoing}
          />

          {displayPrice !== undefined && (
            <Base flexExpandLeft color="base" weight="semibold" size="big">
              {formatPrice(displayPrice, currency)}
            </Base>
          )}
        </Base>
        <Base slot="additional">
          <TooltipContainer
            id="phase-collapsible-date-tooltip"
            renderReference={getReferenceProps => (
              <Base
                {...getReferenceProps()}
                tagName="span"
                color="light"
                size="small"
              >
                {getDateLabel()}
              </Base>
            )}
          >
            {getTooltipProps => (
              <Tooltip
                {...getTooltipProps()}
              >{`${getDateWithUTCOffset()} ${userTimeZone}`}</Tooltip>
            )}
          </TooltipContainer>
        </Base>
      </Choice>
      {checked && (
        <Button small type="button" data-phase={phase.name}>
          {getButtonLabel()}
        </Button>
      )}
    </>
  );
}

export default PhaseChoice;
